/**
 * Профиль игрока: сводка, статистика, результат по дням, история розыгрышей, настройки.
 * Тот же экран администратор открывает для любого пользователя (/admin/users/:id) — тогда
 * вместо настроек показываются действия администратора (блокировка, разблокировка, сброс баланса).
 */
import { useEffect, useState } from 'react';
import type { ProfileDetails } from '@mt/protocol';
import { t, tp } from '../../i18n';
import { api, useLoad } from '../../lib/api';
import { cr, crn, pts } from '../../lib/format';
import { Link } from '../../lib/router';
import { client, useApp } from '../../net/client';
import { ProviderIcon, providerTitle } from '../auth/ProviderIcon';
import { Dialog } from '../Dialog';
import { ago, Avatar, DayBars, dateTime, pct, signedCr, Tile, toneOf } from '../kit';
import { GameList } from './GameList';
import { ResetBalanceButton } from './ResetBalance';

type Tab = 'stats' | 'games' | 'settings';

export function ProfilePage({ userId }: { userId?: string }) {
  const admin = !!userId;
  const { data: p, error, reload, set } = useLoad(() => (userId ? api.admin.user(userId) : api.me()), [userId]);
  const [tab, setTab] = useState<Tab>('stats');
  const liveBalance = useApp((s) => s.profile?.balance);
  const stats = useApp((s) => s.profile?.stats.games);
  // после розыгрыша профиль в шапке обновляется по WebSocket — подтягиваем подробности
  useEffect(() => {
    if (!admin && stats !== undefined) reload();
  }, [stats, admin, reload]);

  if (error) return <div className="page"><p className="notice danger">{error}</p></div>;
  if (!p) return <div className="page center dim">{t('profile.loading')}</div>;
  const balance = admin ? p.balance : (liveBalance ?? p.balance);

  return (
    <div className="page profile-page">
      {admin && (
        <nav className="crumbs">
          <Link to="/admin">{t('profile.crumb.admin')}</Link> <span>/</span> <Link to="/admin?tab=users">{t('profile.crumb.users')}</Link> <span>/</span> <b>{p.name}</b>
        </nav>
      )}
      <ProfileHeader p={p} balance={balance} admin={admin} onChange={set} />

      <div className="segmented tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'stats'} className={tab === 'stats' ? 'on' : ''} onClick={() => setTab('stats')}>
          {t('profile.tab.stats')}
        </button>
        <button role="tab" aria-selected={tab === 'games'} className={tab === 'games' ? 'on' : ''} onClick={() => setTab('games')}>
          {t('profile.tab.games', { n: p.stats.games })}
        </button>
        {!admin && (
          <button role="tab" aria-selected={tab === 'settings'} className={tab === 'settings' ? 'on' : ''} onClick={() => setTab('settings')}>
            {t('profile.tab.settings')}
          </button>
        )}
      </div>

      {tab === 'stats' && <StatsTab p={p} />}
      {tab === 'games' && (
        <section className="panel">
          <GameList
            mode="player"
            load={(o, l) => (admin ? api.admin.userGames(p.id, o, l) : api.myGames(o, l))}
            replayPath={(id) => (admin ? `/admin/games/${id}` : `/profile/games/${id}`)}
          />
        </section>
      )}
      {tab === 'settings' && !admin && <SettingsTab p={p} onChange={set} />}
    </div>
  );
}

function ProfileHeader({ p, balance, admin, onChange }: { p: ProfileDetails; balance: number; admin: boolean; onChange: (p: ProfileDetails) => void }) {
  const [banOpen, setBanOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (fn: () => Promise<ProfileDetails>) => {
    setBusy(true);
    try {
      onChange(await fn());
    } catch (e) {
      client.toast('error', (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel profile-head">
      <Avatar id={p.id} name={p.name} size={64} bot={p.isBot} />
      <div className="profile-id">
        <h1>
          {p.name}
          {p.isBot && <em className="tag">{t('common.bot')}</em>}
          {p.bannedAt && <em className="tag danger">{t('profile.tag.banned')}</em>}
          {p.online && <em className="tag ok">{t('profile.tag.online')}</em>}
        </h1>
        <p className="muted-line">
          {t('profile.since', { date: dateTime(p.createdAt), seen: p.online ? t('profile.seenNow') : ago(p.lastSeenAt) })}
          {admin && (
            <>
              {' '}
              · <code>{p.id}</code>
            </>
          )}
        </p>
        <div className="methods">
          {p.methods.login && <span className="chip-static">{t('profile.method.login', { login: p.methods.login })}</span>}
          {p.methods.providers.map((m) => (
            <span key={m.id} className="chip-static">
              <ProviderIcon id={m.id} /> {m.display ?? m.email ?? m.id}
            </span>
          ))}
        </div>
        {p.bannedAt && (
          <p className="notice danger">
            {p.banReason ? t('profile.bannedAtReason', { date: dateTime(p.bannedAt), reason: p.banReason }) : t('profile.bannedAt', { date: dateTime(p.bannedAt) })}
          </p>
        )}
      </div>
      <div className="profile-balance">
        <span className="dim small">{t('profile.balance')}</span>
        <b className="mono">{cr(balance)}</b>
        {!p.isBot && <ResetBalanceButton name={admin ? p.name : null} balance={balance} reset={() => (admin ? api.admin.resetBalance(p.id) : api.resetBalance())} onDone={onChange} />}
        {admin &&
          !p.isBot &&
          (p.bannedAt ? (
            <button className="btn tiny" disabled={busy} onClick={() => void act(() => api.admin.unban(p.id))}>
              {t('profile.unban')}
            </button>
          ) : (
            <button className="btn tiny danger-outline" disabled={busy} onClick={() => setBanOpen(true)}>
              {t('profile.ban')}
            </button>
          ))}
      </div>
      {banOpen && (
        <Dialog
          title={t('profile.ban.title', { name: p.name })}
          tone="danger"
          confirmText={t('profile.ban')}
          onClose={() => setBanOpen(false)}
          onConfirm={() => {
            setBanOpen(false);
            void act(() => api.admin.ban(p.id, reason));
          }}
        >
          <p>{t('profile.ban.text')}</p>
          <label className="field">
            {t('profile.ban.reason')}
            <input value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder={t('profile.ban.placeholder')} data-autofocus />
          </label>
        </Dialog>
      )}
    </section>
  );
}

function StatsTab({ p }: { p: ProfileDetails }) {
  const s = p.stats;
  const played = s.games - s.cancelled;
  return (
    <div className="stats-layout">
      <section className="panel">
        <h3 className="panel-title">{t('profile.stats.summary')}</h3>
        <dl className="tiles">
          <Tile label={t('profile.stats.games')} value={s.games} hint={t('profile.stats.gamesHint', { n: s.cancelled })} />
          <Tile label={t('profile.stats.winRate')} value={pct(s.winRate)} hint={t('profile.stats.winRateHint')} />
          <Tile label={t('profile.stats.net')} value={signedCr(s.net)} tone={toneOf(s.net)} hint={t('profile.stats.netHint')} />
          <Tile label={t('profile.stats.bestWin')} value={s.bestWin > 0 ? `+${crn(s.bestWin)}` : '—'} tone={s.bestWin > 0 ? 'up' : ''} />
          <Tile label={t('profile.stats.correctDirection')} value={played ? pct(s.correctDirection / Math.max(1, played)) : '—'} />
          <Tile label={t('profile.stats.avgError')} value={s.avgError === null ? '—' : t('profile.stats.pointsValue', { v: pts(s.avgError) })} hint={t('profile.stats.avgErrorHint')} />
          <Tile label={t('profile.stats.folds')} value={s.folds} />
          <Tile label={t('profile.stats.restores')} value={s.restores} hint={t('profile.stats.restoresHint')} />
        </dl>
      </section>
      <section className="panel">
        <h3 className="panel-title">{t('profile.stats.daily')}</h3>
        <DayBars items={p.daily} value={(i) => p.daily[i]!.net} label={t('profile.stats.dailyLabel')} format={(v) => `${signedCr(v)} ${t('common.creditsUnit')}`} />
        <p className="muted-line small">{tp('profile.stats.dailyNote', p.daily.length)}</p>
      </section>
    </div>
  );
}

function SettingsTab({ p, onChange }: { p: ProfileDetails; onChange: (p: ProfileDetails) => void }) {
  const info = useApp((s) => s.info);
  const [name, setName] = useState(p.name);
  const [login, setLogin] = useState(p.methods.login ?? '');
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<ProfileDetails>, ok: string) => {
    setBusy(true);
    try {
      const r = await fn();
      onChange(r);
      client.patchProfile({ name: r.name, showStats: r.showStats });
      client.toast('info', ok);
      return true;
    } catch (e) {
      client.toast('error', (e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const linked = new Set(p.methods.providers.map((m) => m.id));
  const available = info?.auth.providers ?? [];
  const link = async (id: string) => {
    try {
      location.assign((await api.oauthStart(id, true)).url);
    } catch (e) {
      client.toast('error', (e as Error).message);
    }
  };

  return (
    <div className="settings-layout">
      <section className="panel">
        <h3 className="panel-title">{t('profile.settings.visibility')}</h3>
        <label className="switch-row">
          <input
            type="checkbox"
            checked={p.showStats}
            disabled={busy}
            onChange={(e) => {
              const on = e.target.checked;
              const prev = p;
              onChange({ ...p, showStats: on }); // сразу показываем выбор; при ошибке — откат
              void run(() => api.patchMe({ showStats: on }), on ? t('profile.settings.statsShown') : t('profile.settings.statsHidden')).then((ok) => ok || onChange(prev));
            }}
          />
          <span>
            <b>{t('profile.settings.showStats')}</b>
            <small>{t('profile.settings.showStatsNote')}</small>
          </span>
        </label>
        <div className="plaque-preview">
          <Avatar id={p.id} name={p.name} size={28} />
          <span className="who-name">{p.name}</span>
          {p.showStats ? (
            <span className="seat-stats">
              <span title={t('profile.settings.winRateTitle')}>{pct(p.stats.winRate)}</span>
              <span className={toneOf(p.stats.net)}>{signedCr(p.stats.net)}</span>
            </span>
          ) : (
            <span className="dim small">{t('profile.settings.statsHiddenTag')}</span>
          )}
        </div>
      </section>

      <section className="panel">
        <h3 className="panel-title">{t('profile.settings.name')}</h3>
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => api.patchMe({ name }), t('profile.settings.nameChanged'));
          }}
        >
          <input value={name} maxLength={24} onChange={(e) => setName(e.target.value)} aria-label={t('profile.settings.name')} />
          <button className="btn" disabled={busy || name.trim() === p.name || name.trim().length < 2}>
            {t('profile.settings.save')}
          </button>
        </form>
        <p className="muted-line small">{t('profile.settings.nameNote')}</p>
      </section>

      <section className="panel">
        <h3 className="panel-title">{p.methods.login ? t('profile.settings.changePassword') : t('profile.settings.setPassword')}</h3>
        <form
          className="stack-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (pw !== pw2) return client.toast('error', t('profile.settings.passwordMismatch'));
            void run(() => api.setPassword({ login: p.methods.login ? undefined : login, current: p.methods.login ? current : undefined, password: pw }), t('profile.settings.passwordSaved')).then((ok) => {
              if (ok) (setCurrent(''), setPw(''), setPw2(''));
            });
          }}
        >
          {!p.methods.login && (
            <label className="field">
              {t('profile.settings.login')}
              <input autoComplete="username" value={login} maxLength={32} onChange={(e) => setLogin(e.target.value)} />
            </label>
          )}
          {p.methods.login && (
            <label className="field">
              {t('profile.settings.currentPassword')}
              <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
            </label>
          )}
          <label className="field">
            {t('profile.settings.newPassword')}
            <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
          </label>
          <label className="field">
            {t('profile.settings.newPasswordAgain')}
            <input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </label>
          <button className="btn primary" disabled={busy || pw.length < 8 || !pw2 || (p.methods.login ? !current : login.trim().length < 3)}>
            {t('profile.settings.savePassword')}
          </button>
        </form>
      </section>

      <section className="panel">
        <h3 className="panel-title">{t('profile.settings.providers')}</h3>
        {available.length === 0 && p.methods.providers.length === 0 && <p className="muted-line">{t('profile.settings.noProviders')}</p>}
        <ul className="provider-list">
          {p.methods.providers.map((m) => (
            <li key={m.id}>
              <ProviderIcon id={m.id} />
              <span>
                <b>{available.find((a) => a.id === m.id)?.title ?? m.id}</b>
                <small>
                  {m.display ?? ''} {m.email ? `· ${m.email}` : ''} · {t('profile.settings.linkedSince', { date: dateTime(m.linkedAt) })}
                </small>
              </span>
              <button className="btn tiny ghost" disabled={busy} onClick={() => void run(() => api.unlink(m.id), t('profile.settings.unlinked'))}>
                {t('profile.settings.unlink')}
              </button>
            </li>
          ))}
          {available
            .filter((a) => !linked.has(a.id))
            .map((a) => (
              <li key={a.id}>
                <ProviderIcon id={a.id} />
                <span>
                  <b>{providerTitle(a)}</b>
                  <small>{t('profile.settings.notLinked')}</small>
                </span>
                <button className="btn tiny" onClick={() => void link(a.id)}>
                  {t('profile.settings.link')}
                </button>
              </li>
            ))}
        </ul>
      </section>
    </div>
  );
}
